import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import { Box, TextField, Typography } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import Switch from '@mui/material/Switch';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import { accordionSummaryClasses } from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { arrayMoveImmutable } from 'array-move';
import { JobApplicationStatus, jobApplicationStatusLabels } from '@modules/jobApplications/statuses';
import Menu from '@mui/material/Menu';
import DragMouseIcon from '@components/Icon/DragMouse';
import TriangleIcon from '@components/Icon/Triangle';
import ThreeDotsIcon from '@components/Icon/ThreeDots';
import PlusIcon from '@components/Icon/Plus';
import IconButton from '@mui/material/IconButton';
import DragIcon from '@components/Icon/Drag';
import {
    SortableContainer,
    // SortableElement,
    // SortableHandle
} from 'react-sortable-hoc';
import CloseIcon from '@components/Icon/Close';

const listColorOption = [
    { label: 'White', color: 'rgba(124, 121, 139, 1)' },
    { label: 'White', color: 'rgba(255, 255, 255, 1)' },
    { label: 'Blue', color: 'rgba(47, 128, 237, 1)' },
    { label: 'Aqua', color: 'rgba(95, 212, 244, 1)' },
    { label: 'Orange', color: 'rgba(242, 153, 74, 1)' },
    { label: 'Yellow', color: 'rgba(255, 232, 28, 1)' },
    { label: 'Green', color: 'rgba(70, 167, 88, 1)' },
    { label: 'Dark Green', color: 'rgba(17, 137, 153, 1)' },
    { label: 'Light Purple', color: 'rgba(220, 184, 255, 1)' },
    { label: 'Purple', color: 'rgba(66, 57, 176, 1)' },
];

const MenuWrapper = styled(Menu)(({ theme }) => ({
    '& .MuiPaper-root': {
        borderRadius: '16px',
        background: 'rgba(34, 36, 38, 1)',
        padding: '0px 0px 0px 0px',
        maxWidth: '305px',
        // boxShadow: 'none',
        boxShadow: '0px 16px 40px 0px rgba(0, 0, 0, 1)',
    },
    '& .MuiList-root': {
        padding: 0,
    },

    '& .MuiMenuItem-root': {
        padding: '10.5px 20px',
        borderRadius: '8px',
    },

    '& .menu-item': {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        fontWeight: 700,
        fontSize: '16px',
        lineHeight: '20px',

        '&:hover': {
            background: theme.palette.backgroundPilsurface.main,
        },
    },

    '& .MuiAccordionSummary-content': {
        marginTop: '10px',
        marginBottom: '10px',
    },
}));

const DialogWrapper = styled(Dialog)(({ theme }) => ({
    '& .MuiPaper-root': {
        borderRadius: '12px',
        background: 'none',
        backgroundImage: 'none',
        width: '100%',
        maxWidth: '691px',
    },

    '& .content': {
        padding: '32px 24px',
        background: 'rgba(34, 36, 38, 1)',
        borderRadius: '12px',
        gap: 0,
    },

    '& .header': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        fontWeight: 700,
        fontSize: '28px',
        lineHeight: '100%',
        color: theme.palette.textPrimary.main,
        fontFamily: theme.typography.fontFamilyBrand,
        textAlign: 'left',
        marginBottom: '28px',
        width: '100%',
        cursor: 'text',
    },

    '& .switch-wrapper': {
        width: '100%',
        display: 'flex',
        justifyContent: 'start',
        alignItems: 'center',
        gap: '6px',
        fontWeight: 600,
        fontSize: '14px',
        lineHeight: '20px',
        color: theme.palette.textPrimary.main,
        margin: 0,
    },

    '& .MuiSwitch-root': {
        padding: '6px px 6px 0px',
        marginLeft: '0px !important',
        marginRight: '0px !important',
    },
}));

const CustomSwitch = styled(Switch)(({ theme }) => ({
    margin: 0,
    '& .MuiSwitch-thumb': {
        backgroundColor: theme.palette.neutralWhite.main,
    },
    '& .MuiSwitch-track': {
        backgroundColor: theme.palette.neutralGray.main,
    },
    '&.Mui-checked .MuiSwitch-track': {
        backgroundColor: theme.palette.primaryPurple.main,
    },
}));

const Accordion = styled((props: AccordionProps) => <MuiAccordion disableGutters elevation={0} square {...props} />)(
    ({ theme }) => ({
        width: '100%',
        border: `none`,
        background: 'transparent',
        fontWeight: 600,
        fontSize: '14px',
        lineHeight: '20px',

        span: {
            fontWeight: 600,
            fontSize: '14px',
            lineHeight: '20px',
        },

        '& .MuiAccordionDetails-root': {
            padding: '0px',
        },

        '&:not(:last-child)': {
            borderBottom: 0,
        },
        '&::before': {
            display: 'none',
        },
        '& .MuiPaper-root': {
            padding: '0px !important',
        },

        '& .MuiAccordionSummary-root': {
            padding: '0px !important',
            width: '100%',

            '& ::before': {
                height: '0px',
            },
        },
    }),
);

const BulletColor = styled(Box)(({ theme }) => ({
    borderRadius: '50%',
    width: '20px',
    height: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    '& .center-bullet': {
        width: '12px',
        height: '12px',
        borderRadius: '50%',
    },
}));

const DragAndDropContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    width: '100%',
    marginTop: '16px',
}));

interface StatusManagementModalProps {
    open: boolean;
    setOpen: (prev: boolean) => void;
    onSubmit: (jobNumbers: string) => void;
    isLoadingSubmit?: boolean;
}

const DraggableWrapper: React.FC<{
    value: string;
    columnId: string;
    children: React.ReactNode;
}> = ({ value, columnId, children }) => {
    const handleDragStart = (e: React.DragEvent) => {
        e.dataTransfer.setData('text', JSON.stringify([value, columnId]));
        e.dataTransfer.effectAllowed = 'move';
    };
    return (
        <div draggable onDragStart={handleDragStart}>
            {children}
        </div>
    );
};

const CardOption = styled(Box)(({ theme }) => ({
    width: '100%',
    minWidth: '289px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: '18px',
    fontWeight: 700,
    padding: '12px 16px',
    color: theme.palette.neutralLightGrey.main,
    border: `1px solid ${theme.palette.border.main}`,
    borderRadius: '8px',
}));

interface SortableListProps {
    items: { key: string; value: string }[];
    columnId: string;
    onDropItem: (item: { key: string; value: string }, fromColumn: string, toColumn: string) => void;
    color: (columnId: string) => string;
    setAnchorEl: (el: HTMLElement | null) => void;
}

const SortableList = SortableContainer<SortableListProps>(({ items, columnId, onDropItem, setAnchorEl }) => {
    const [dragOver, setDragOver] = useState(false);
    const [dropPosition, setDropPosition] = useState<number | null>(null);

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(true);
        // Calculate drop position
        const rect = e.currentTarget.getBoundingClientRect();
        const y = e.clientY - rect.top;
        const itemHeight = rect.height / items.length;
        setDropPosition(Math.floor(y / itemHeight));
    };

    const handleDragLeave = () => {
        setDragOver(false);
        setDropPosition(null);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);
        setDropPosition(null);
        const data = e.dataTransfer.getData('text');
        const [item, fromColumn] = JSON.parse(data);
        if (fromColumn !== columnId) {
            onDropItem(item, fromColumn, columnId);
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
            }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            style={{ minHeight: '100%' }}>
            {items.length === 0 && (
                <Box
                    sx={{
                        width: '100%',
                        minWidth: '289px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        fontSize: '14px',
                        fontWeight: 700,
                        padding: '31px 70px 33px',
                        color: theme => theme.palette.neutralLightGrey.main,
                    }}>
                    <DragMouseIcon />
                    <Box sx={{ marginTop: '4px' }}>Drag to change status</Box>
                </Box>
            )}
            {items.length > 0 &&
                items.map((value, index) => (
                    <React.Fragment key={`item-${index}`}>
                        {dragOver && dropPosition === index && <DropIndicator style={{ opacity: 1 }} />}
                        <DraggableWrapper value={value} columnId={columnId}>
                            <CardOption>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Box sx={{ marginRight: '16px' }}>
                                        <DragIcon />
                                    </Box>
                                    <BulletColor
                                        sx={{
                                            border: theme => {
                                                switch (columnId) {
                                                    case 'Contact':
                                                        return `1px solid ${theme.palette.colorSecondary50.main}`;
                                                    case 'Enntretien':
                                                        return `1px solid ${theme.palette.othersBlue.main}`;
                                                    case 'Essai':
                                                        return `1px solid ${theme.palette.warningOrange.main}`;
                                                    case 'Proposition':
                                                        return `1px solid ${theme.palette.purpose.main}`;
                                                    case "Période d'essai":
                                                        return `1px solid rgba(84, 71, 222, 1)`;
                                                    default:
                                                        return `1px solid rgba(84, 71, 222, 1)`;
                                                }
                                            },
                                        }}>
                                        <Box
                                            className="center-bullet"
                                            sx={{
                                                backgroundColor: theme => {
                                                    switch (columnId) {
                                                        case 'Contact':
                                                            return theme.palette.colorSecondary50.main;
                                                        case 'Enntretien':
                                                            return theme.palette.othersBlue.main;
                                                        case 'Essai':
                                                            return theme.palette.warningOrange.main;
                                                        case 'Proposition':
                                                            return theme.palette.purpose.main;
                                                        case "Période d'essai":
                                                            return 'rgba(84, 71, 222, 1)';
                                                        default:
                                                            return 'rgba(84, 71, 222, 1)';
                                                    }
                                                },
                                                borderColor: theme => {
                                                    switch (columnId) {
                                                        case 'Contact':
                                                            return theme.palette.colorSecondary50.main;
                                                        case 'Enntretien':
                                                            return theme.palette.othersBlue.main;
                                                        case 'Essai':
                                                            return theme.palette.warningOrange.main;
                                                        case 'Proposition':
                                                            return theme.palette.purpose.main;
                                                        case "Période d'essai":
                                                            return 'rgba(84, 71, 222, 1)';
                                                        default:
                                                            return 'rgba(84, 71, 222, 1)';
                                                    }
                                                },
                                            }}></Box>
                                    </BulletColor>
                                    <Box sx={{ marginLeft: '16px' }}>{value.value}</Box>
                                </Box>
                                <Box>
                                    <IconButton
                                        onClick={e => {
                                            e.stopPropagation(); // This prevents the event from bubbling up to the AccordionSummary
                                            setAnchorEl(e.currentTarget);
                                        }}>
                                        <ThreeDotsIcon />
                                    </IconButton>
                                </Box>
                            </CardOption>
                        </DraggableWrapper>
                    </React.Fragment>
                ))}
            {dragOver && dropPosition === items.length && <DropIndicator style={{ opacity: 1 }} />}
        </Box>
    );
});

const DropIndicator = styled(Box)({
    height: '4px',
    backgroundColor: '#1976d2',
    margin: '4px 0',
    borderRadius: '2px',
    opacity: 0,
    transition: 'opacity 0.2s',
});

/**
 * Status Management Modal Component
 * 
 * Modal for managing and configuring job application statuses.
 * Allows creation and organization of recruitment workflow stages.
 * 
 * Features:
 * - Drag and drop status organization
 * - Custom status creation
 * - Color coding for statuses
 * - Accordion-based grouping
 * 
 * Note: Despite the original name "ManageStatusJobOffer", this component
 * manages JobApplicationStatus enums and belongs to the JobApplications domain.
 */
const StatusManagementModal = ({ open, setOpen, onSubmit, isLoadingSubmit }: StatusManagementModalProps) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const openMenu = Boolean(anchorEl);
    const [showTextFieldForColumn, setShowTextFieldForColumn] = useState<string | null>(null);
    const handleClose = () => {
        setOpen(false);
    };

    const [columns, setColumns] = useState({
        Contact: [
            { key: 'Item 1-1', value: jobApplicationStatusLabels[JobApplicationStatus.New] },
            { key: 'Item 1-2', value: 'Vivier' },
            { key: 'Item 1-3', value: jobApplicationStatusLabels[JobApplicationStatus.Interview] },
            { key: 'Item 1-4', value: jobApplicationStatusLabels[JobApplicationStatus.Trial] },
        ],
        Enntretien: [
            { key: 'Item 2-1', value: jobApplicationStatusLabels[JobApplicationStatus.New] },
            { key: 'Item 2-2', value: 'Vivier' },
            { key: 'Item 2-3', value: jobApplicationStatusLabels[JobApplicationStatus.Interview] },
            { key: 'Item 2-4', value: jobApplicationStatusLabels[JobApplicationStatus.Trial] },
        ],
        Essai: [
            { key: 'Item 3-1', value: jobApplicationStatusLabels[JobApplicationStatus.New] },
            { key: 'Item 3-2', value: 'Vivier' },
            { key: 'Item 3-3', value: jobApplicationStatusLabels[JobApplicationStatus.Interview] },
            { key: 'Item 3-4', value: jobApplicationStatusLabels[JobApplicationStatus.Trial] },
        ],
        Proposition: [
            { key: 'Item 4-1', value: jobApplicationStatusLabels[JobApplicationStatus.New] },
            { key: 'Item 4-2', value: 'Vivier' },
            { key: 'Item 4-3', value: jobApplicationStatusLabels[JobApplicationStatus.Interview] },
            { key: 'Item 4-4', value: jobApplicationStatusLabels[JobApplicationStatus.Trial] },
        ],
        "Période d'essai": [
            { key: 'Item 5-1', value: jobApplicationStatusLabels[JobApplicationStatus.New] },
            { key: 'Item 5-2', value: 'Vivier' },
            { key: 'Item 5-3', value: jobApplicationStatusLabels[JobApplicationStatus.Interview] },
            { key: 'Item 5-4', value: jobApplicationStatusLabels[JobApplicationStatus.Trial] },
        ],
    });

    const handleColumnSortEnd = (columnId: string, { oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => {
        setColumns(prev => {
            const newItems = arrayMoveImmutable(prev[columnId], oldIndex, newIndex);
            return { ...prev, [columnId]: newItems };
        });
    };

    const handleDropItem = (item: { key: string; value: string }, fromColumn: string, toColumn: string) => {
        let updatedStatus = {
            ...item,
        };

        setColumns(prev => {
            // Remove from source column
            const newColumns = { ...prev };
            newColumns[fromColumn] = newColumns[fromColumn].filter(
                (i: { key: string; value: string }) => i.key !== item.key,
            );

            // Add to target column
            newColumns[toColumn] = [...newColumns[toColumn], updatedStatus];

            return newColumns;
        });
    };

    return (
        <DialogWrapper onClose={() => { }} open={open}>
            <Box className="content">
                <Box className="header">
                    <Box>
                        Créez une nouvelle étape de <br></br> votre parcours de recrutement
                    </Box>
                    <IconButton onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Box className="switch-wrapper">
                    <CustomSwitch defaultChecked sx={{ margin: 0 }} />
                    <Box>Voir toutes les étapes</Box>
                </Box>
                {Object.entries(columns).map(([columnId, items]) => (
                    <Accordion disableGutters key={columnId}>
                        <AccordionSummary
                            expandIcon={<TriangleIcon />}
                            sx={{
                                border: 'none',
                                flexDirection: 'row-reverse',
                                [`& .${accordionSummaryClasses.expandIconWrapper}`]: {
                                    transform: 'rotate(270deg)',
                                },
                                [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]:
                                {
                                    transform: 'rotate(360deg)',
                                },
                            }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    marginLeft: '12px',
                                    justifyContent: 'space-between',
                                    width: '100%',
                                }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <BulletColor
                                        sx={{
                                            border: theme => {
                                                switch (columnId) {
                                                    case 'Contact':
                                                        return `1px solid ${theme.palette.colorSecondary50.main}`;
                                                    case 'Enntretien':
                                                        return `1px solid ${theme.palette.othersBlue.main}`;
                                                    case 'Essai':
                                                        return `1px solid ${theme.palette.warningOrange.main}`;
                                                    case 'Proposition':
                                                        return `1px solid ${theme.palette.purpose.main}`;
                                                    case "Période d'essai":
                                                        return `1px solid rgba(84, 71, 222, 1)`;
                                                    default:
                                                        return `1px solid rgba(84, 71, 222, 1)`;
                                                }
                                            },
                                        }}>
                                        <Box
                                            className="center-bullet"
                                            sx={{
                                                backgroundColor: theme => {
                                                    switch (columnId) {
                                                        case 'Contact':
                                                            return theme.palette.colorSecondary50.main;
                                                        case 'Enntretien':
                                                            return theme.palette.othersBlue.main;
                                                        case 'Essai':
                                                            return theme.palette.warningOrange.main;
                                                        case 'Proposition':
                                                            return theme.palette.purpose.main;
                                                        case "Période d'essai":
                                                            return 'rgba(84, 71, 222, 1)';
                                                        default:
                                                            return 'rgba(84, 71, 222, 1)';
                                                    }
                                                },
                                                borderColor: theme => {
                                                    switch (columnId) {
                                                        case 'Contact':
                                                            return theme.palette.colorSecondary50.main;
                                                        case 'Enntretien':
                                                            return theme.palette.othersBlue.main;
                                                        case 'Essai':
                                                            return theme.palette.warningOrange.main;
                                                        case 'Proposition':
                                                            return theme.palette.purpose.main;
                                                        case "Période d'essai":
                                                            return 'rgba(84, 71, 222, 1)';
                                                        default:
                                                            return 'rgba(84, 71, 222, 1)';
                                                    }
                                                },
                                            }}></Box>
                                    </BulletColor>
                                    <Typography fontWeight={600} fontSize={18}>
                                        {columnId}
                                    </Typography>
                                </Box>
                                <Box>
                                    <IconButton
                                        onClick={e => {
                                            e.stopPropagation(); // This prevents the event from bubbling up to the AccordionSummary
                                            setShowTextFieldForColumn(columnId);
                                        }}>
                                        <PlusIcon />
                                    </IconButton>
                                    <IconButton
                                        onClick={e => {
                                            e.stopPropagation(); // This prevents the event from bubbling up to the AccordionSummary
                                            setAnchorEl(e.currentTarget);
                                        }}>
                                        <ThreeDotsIcon />
                                    </IconButton>
                                </Box>
                            </Box>
                            <MenuWrapper
                                anchorEl={anchorEl}
                                open={openMenu}
                                onClose={() => setAnchorEl(null)}
                                className="menu"
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                                onClick={e => e.stopPropagation()}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '10px',
                                        padding: '12px 16px',
                                    }}>
                                    <Box
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: '15px',
                                            lineHeight: '100%',
                                            color: 'rgba(124, 121, 139, 1)',
                                        }}>
                                        Colors
                                    </Box>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        {listColorOption.map(option => (
                                            <Box
                                                key={option.label}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '12px',
                                                    padding: '8px 8px 8px 0px',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                                        borderRadius: '8px',
                                                        cursor: 'pointer',
                                                    },
                                                }}>
                                                <BulletColor
                                                    sx={{
                                                        border: `1px solid ${option.color}`,
                                                    }}>
                                                    <Box
                                                        className="center-bullet"
                                                        sx={{
                                                            backgroundColor: option.color,
                                                            borderColor: theme => {
                                                                switch (columnId) {
                                                                    case 'Contact':
                                                                        return theme.palette.colorSecondary50.main;
                                                                    case 'Enntretien':
                                                                        return theme.palette.othersBlue.main;
                                                                    case 'Essai':
                                                                        return theme.palette.warningOrange.main;
                                                                    case 'Proposition':
                                                                        return theme.palette.purpose.main;
                                                                    case "Période d'essai":
                                                                        return 'rgba(84, 71, 222, 1)';
                                                                    default:
                                                                        return 'rgba(84, 71, 222, 1)';
                                                                }
                                                            },
                                                        }}></Box>
                                                </BulletColor>
                                                <Box
                                                    sx={{
                                                        fontWeight: 700,
                                                        fontSize: '16px',
                                                        color: theme => theme.palette.textPrimary.main,
                                                    }}>
                                                    {option.label}
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            </MenuWrapper>
                        </AccordionSummary>
                        <AccordionDetails>
                            <DragAndDropContainer>
                                {showTextFieldForColumn === columnId && <TextField>Halo</TextField>}
                                <SortableList
                                    items={items}
                                    columnId={columnId}
                                    onSortEnd={({ oldIndex, newIndex }) =>
                                        handleColumnSortEnd(columnId, { oldIndex, newIndex })
                                    }
                                    onDropItem={handleDropItem}
                                    useDragHandle
                                    lockAxis="y"
                                    distance={5}
                                    helperClass="sortable-helper"
                                    setAnchorEl={setAnchorEl}
                                    color={() => {
                                        switch (columnId) {
                                            case 'Contact':
                                                return 'rgba(203, 203, 203, 1)';
                                            case 'Enntretien':
                                                return 'rgba(47, 128, 237, 1)';
                                            case 'Essai':
                                                return 'rgba(242, 153, 74, 1)';
                                            case 'Proposition':
                                                return 'rgba(17, 137, 153, 1)';
                                            case "Période d'essai":
                                                return 'rgba(84, 71, 222, 1)';
                                            default:
                                                return 'rgba(84, 71, 222, 1)';
                                        }
                                    }}
                                />
                            </DragAndDropContainer>
                        </AccordionDetails>
                    </Accordion>
                ))}
            </Box>
        </DialogWrapper>
    );
};

export default StatusManagementModal;
